/**
 * Gold economy: what games pay, what gift boxes contain and how they are rolled.
 * Gold is earned by playing and by watching rewarded ads; it can never be
 * bought with real money.
 * Pure (randomness is injected), so it is unit tested.
 */
import { COSMETICS, type Cosmetic } from './cosmetics.ts';

export const GOLD_FOR = { win: 20, draw: 8, loss: 3 } as const;
/** Extra gold for the first win of each (Turkey-time) day. */
export const FIRST_WIN_BONUS = 30;
/** Only this many games per day pay gold, so the economy cannot be farmed. */
export const DAILY_PAID_GAMES = 25;
/**
 * Changing your username costs gold: a few days of regular play (about
 * 100–150 gold a day), around the middle of the shop's price range.
 */
export const RENAME_PRICE = 500;

/** Gold for each rewarded ad watched to the end. No daily limit. */
export const AD_GOLD = 50;
/**
 * The server only accepts an ad as finished this long after it started, so a
 * reward cannot be claimed without at least sitting through an ad's length.
 */
export const AD_MIN_SECONDS = 8;

export function goldForGame(score: 0 | 0.5 | 1, opts: { firstWinToday: boolean; paidGamesToday: number }): number {
  if (opts.paidGamesToday >= DAILY_PAID_GAMES) return 0;
  const base = score === 1 ? GOLD_FOR.win : score === 0.5 ? GOLD_FOR.draw : GOLD_FOR.loss;
  return base + (score === 1 && opts.firstWinToday ? FIRST_WIN_BONUS : 0);
}

// ------------------------------------------------------------- gift boxes

export type BoxTier = 'gold' | 'silver' | 'bronze';
export type BoxPeriod = 'day' | 'week';

export const TIER_FOR_RANK: Record<1 | 2 | 3, BoxTier> = { 1: 'gold', 2: 'silver', 3: 'bronze' };

export interface BoxOdds {
  gold: [min: number, max: number];
  /** Chance (0..1) that the box also holds an avatar or frame. */
  itemChance: number;
}

/** Published in the UI so players know exactly what a box can contain. */
export const BOX_ODDS: Record<BoxPeriod, Record<BoxTier, BoxOdds>> = {
  day: {
    gold: { gold: [150, 300], itemChance: 0.04 },
    silver: { gold: [90, 180], itemChance: 0.025 },
    bronze: { gold: [50, 120], itemChance: 0.015 },
  },
  week: {
    gold: { gold: [500, 900], itemChance: 0.12 },
    silver: { gold: [300, 600], itemChance: 0.08 },
    bronze: { gold: [180, 400], itemChance: 0.05 },
  },
};

/** Avatars and frames a box can drop, weighted so legendary items are rarest. */
export const BOX_ITEM_WEIGHT: Record<Cosmetic['rarity'], number> = { common: 0, rare: 6, epic: 3, legendary: 1 };

export function boxItemPool(): Cosmetic[] {
  return COSMETICS.filter((c) => (c.kind === 'avatar' || c.kind === 'frame') && c.source !== 'starter' && BOX_ITEM_WEIGHT[c.rarity] > 0);
}

export interface BoxReward {
  gold: number;
  item: string | null;
  /** The rolled item was already owned and was turned into gold instead. */
  duplicateGold: number;
}

/** Gold paid instead of an item the player already has. */
export const DUPLICATE_GOLD: Record<Cosmetic['rarity'], number> = { common: 50, rare: 150, epic: 300, legendary: 600 };

export function rollBox(
  period: BoxPeriod,
  tier: BoxTier,
  owned: ReadonlySet<string>,
  random: () => number = Math.random,
): BoxReward {
  const odds = BOX_ODDS[period][tier];
  const [min, max] = odds.gold;
  const gold = min + Math.floor(random() * (max - min + 1));
  if (random() >= odds.itemChance) return { gold, item: null, duplicateGold: 0 };

  const pool = boxItemPool();
  const total = pool.reduce((sum, c) => sum + BOX_ITEM_WEIGHT[c.rarity], 0);
  let pick = random() * total;
  let item = pool[pool.length - 1];
  for (const c of pool) {
    pick -= BOX_ITEM_WEIGHT[c.rarity];
    if (pick < 0) {
      item = c;
      break;
    }
  }
  if (owned.has(item.id)) return { gold, item: null, duplicateGold: DUPLICATE_GOLD[item.rarity] };
  return { gold, item: item.id, duplicateGold: 0 };
}
